import { useNavigate } from "react-router-dom";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, SchemaOf, string, boolean } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppSelector } from "../../../app/hooks";
import { useApi } from "../../../services/useApi";
import { Colors } from "../../../styles/colors";
import { AssessmentShell } from "../components/AssessmentShell";
import { LBtn } from "../../../common/components/LBtn";
import { LTitle } from "../../../common/components/LTitle";
import { LCheckbox } from "../../../common/components/LCheckbox";
import { LSubTitle } from "../../../common/components/LSubtitle";
import { LTextareaField } from "../../../common/components/LTextareaField";
import { NavItems } from "@helpers/navigation";
import { AssessmentStatus } from "@store/generalTypes";

interface ReviewNote {
    isForReview?: boolean;
    note?: string;
}

const formSchema: SchemaOf<ReviewNote> = object({
    isForReview: boolean().optional(),
    note: string()
        .max(500)
        .when("isForReview", (val, schema) => {
            return val ? schema.required("Please add a note explaining why this test result needs review") : schema;
        })
});

export const AssessmentReview = (): JSX.Element => {
    const navigate = useNavigate();
    const { reviewAssessment, updateAssessmentStatus, loading } = useApi();
    const { assessment, examinee } = useAppSelector((state) => ({
        assessment: state.assessment,
        examinee: state.newExaminee
    }));

    const validateMethods = useForm<ReviewNote>({
        resolver: yupResolver(formSchema)
    });

    const formSubmitHandler: SubmitHandler<ReviewNote> = async (data: ReviewNote) => {
        if (data.note) {
            await reviewAssessment(assessment.locationId, assessment.id, data.note);
        }

        if (data.isForReview) {
            await updateAssessmentStatus(assessment.locationId, assessment.id, AssessmentStatus.ReviewNeeded);
        }

        navigate(NavItems.Admin.Manage.Overview.path);
    };

    return (
        <div style={{ backgroundColor: Colors.primaryBlue }}>
            <FormProvider {...validateMethods}>
                <AssessmentShell>
                    <div className="is-flex is-flex-direction-column">
                        <LTitle overrideSize="4rem" theme="dark" verticalSpacing="3">
                            Comment On This Assessment
                        </LTitle>
                        <div className="box p-4 container">
                            <LTitle size="1" verticalSpacing="5">
                                {`${examinee.contactInfo.firstName} ${examinee.contactInfo.lastName}`}:
                            </LTitle>
                            <LSubTitle size="4" className="mb-3">
                                Optional
                            </LSubTitle>

                            <div className="is-flex is-flex-direction-column has-text-left mx-6">
                                <LSubTitle color="has-text-dark-blue" size="4" verticalSpacing="3" className="mx-6">
                                    Add any notes describing any events that positively or negatively affected the
                                    assessment
                                </LSubTitle>
                                <LSubTitle color="has-text-dark-blue" size="5" className="mx-6 mb-1">
                                    Any notes you add will be visible to anyone viewing these results.
                                </LSubTitle>
                                <LSubTitle color="has-text-dark-blue" size="5" className="mx-6 mb-1">
                                    Refrain from using the examinee’s name in any notes.
                                </LSubTitle>
                                <div className="is-flex is-flex-direction-column mx-6">
                                    <form onSubmit={validateMethods.handleSubmit(formSubmitHandler)}>
                                        <LTextareaField
                                            label=""
                                            inputName="note"
                                            placeholder="Add Note"
                                            rows={10}
                                            maxLength={500}
                                            counter
                                            theme="dark"
                                        />
                                        <div className="is-flex is-justify-content-space-between my-5">
                                            <LCheckbox
                                                label="Mark for review"
                                                inputName={"isForReview"}
                                                rightSided
                                                tooltip="Mark for Review to alert clinicians and researchers that extra attention is required for these results."
                                                tooltipDirection="up"
                                            />
                                            <LBtn type="submit" loading={loading}>
                                                Done
                                            </LBtn>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </AssessmentShell>
            </FormProvider>
        </div>
    );
};
